/*!
 * 級別 Top 玩家
 *
 * 每個盲注級別 Rake 最高的前 N 位「真人」玩家（排除 EcoBot），
 * 供營運同事拿玩家 ID 去查該玩家的遊戲歷史、分析上桌行為。
 *
 * 設計：沿用「俱樂部管理」(TinyClubConfig.js) 的 design pattern —
 *       COLORS token、inputStyle/labelStyle/thStyle/tdStyle、EnhancedNav Tab、統計卡片。
 *
 * UX 原則（刻意設計）：
 *   1. 進頁只載入「週期清單」這種輕量資料，**不自動跑聚合**，按「查詢」才打 API。
 *   2. 後端只回每級別前 N 名（約 120 列），不回全量讓前端排序 → payload 與記憶體都小。
 *   3. 條件改了但還沒查 → 顯示「條件已變更」提示，不偷偷用舊資料誤導人。
 *   4. 級別用 Tab 分開，一次只 render 一個級別的表格。
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Table,
  TabContent,
  TabPane,
} from "reactstrap";

import axios from "axios";
import classnames from "classnames";
import TinyLoading, { TinyBusy } from "components/tiny/TinyLoading";
import TinyWeekRangePicker from "../../components/tiny/TinyWeekRangePicker";
import { windowNav } from "../../components/tiny/weekWindowUtils";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import TinyStatStrip from "components/tiny/TinyStatStrip";
const API_BASE = "/api/tiny";

// ==================== 共用樣式常量（與 TinyClubConfig 一致）====================
const COLORS = {
  cardBg: '#1e2530',
  headerBg: '#252d3a',
  inputBg: '#1a1f2b',
  border: 'rgba(255,255,255,0.1)',
  borderInput: 'rgba(255,255,255,0.2)',
  textPrimary: '#fff',
  textSecondary: 'rgba(255,255,255,0.8)',
  textMuted: 'rgba(255,255,255,0.5)',
  textDimmed: 'rgba(255,255,255,0.4)',
  accent: '#667eea',
  accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  success: '#2dce89',
  danger: '#f5365c',
  info: '#11cdef',
  warning: '#fb6340',
};

const inputStyle = {
  background: COLORS.inputBg,
  color: COLORS.textPrimary,
  border: `1px solid ${COLORS.borderInput}`,
  borderRadius: '6px',
};

const labelStyle = {
  color: COLORS.textSecondary,
  fontWeight: 500,
  fontSize: '13px',
};

const thStyle = {
  borderColor: COLORS.border,
  padding: '10px 8px',
  fontWeight: 600,
  fontSize: '12px',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  borderColor: 'rgba(255,255,255,0.05)',
  padding: '10px 8px',
  verticalAlign: 'middle',
};

const primaryBtnStyle = (disabled) => ({
  background: disabled ? 'rgba(102,126,234,0.5)' : COLORS.accentGradient,
  border: 'none',
  padding: '10px 16px',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '14px',
  boxShadow: disabled ? 'none' : '0 4px 12px rgba(102,126,234,0.3)',
  marginBottom: 0,
  // 任何解析度都不讓「查詢」被拆成兩行
  whiteSpace: 'nowrap',
  minWidth: '92px',
});

const subtleBtnStyle = {
  background: 'rgba(102,126,234,0.15)',
  border: '1px solid rgba(102,126,234,0.4)',
  color: COLORS.accent,
  padding: '6px 16px',
  borderRadius: '6px',
  fontWeight: 500,
  marginBottom: 0,
};

// 表格內的單筆複製鈕：只有 icon，不佔版面
const iconCopyBtnStyle = (copied) => ({
  background: 'transparent',
  border: 'none',
  color: copied ? COLORS.success : COLORS.textDimmed,
  padding: '0 4px',
  marginLeft: '6px',
  marginBottom: 0,
  boxShadow: 'none',
  fontSize: '12px',
  lineHeight: 1,
  verticalAlign: 'middle',
});

// ==================== Enhanced Nav（ECOBOT 風格，同俱樂部管理）====================
const EnhancedNav = ({ children, ...props }) => (
  <Nav {...props} className="custom-nav mb-1 position-relative"
    style={{ borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', flexWrap: 'wrap', background: '#111822', padding: '4px', border: `1px solid ${COLORS.border}`, cursor: 'pointer' }}>
    {children}
  </Nav>
);
const EnhancedNavItem = ({ children, ...props }) => (
  <NavItem {...props} style={{ margin: '2px', position: 'relative', zIndex: 1 }}>{children}</NavItem>
);
// ⚠️ 原本這裡有一份 EnhancedNavLink（四份複製之一）。已改用 TinyPageTabs。

// ==================== 統計卡片 ====================
const numberWithCommas = (num) => {
  if (num === null || num === undefined) return "0";
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const CATEGORIES = [
  { value: "NLH", label: "NLH" },
  { value: "AOF", label: "AOF" },
  { value: "PLO", label: "PLO" },
  { value: "ALL", label: "全部類別" },
];

const TOP_OPTIONS = [10, 20, 50];

// 級別下拉：ALL_HIGH = 5/10 以上（bb>=10），ALL = 全部含 1/2、3/6
const LEVEL_ALL_HIGH = "__all_high__";
const LEVEL_ALL = "__all__";

function TinyTopPlayers() {
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [appliedWeeks, setAppliedWeeks] = useState([]);

  const [category, setCategory] = useState("NLH");
  const [levelValue, setLevelValue] = useState(LEVEL_ALL_HIGH);
  const [topN, setTopN] = useState(20);

  const [availableLevels, setAvailableLevels] = useState([]);
  const [loadingLevels, setLoadingLevels] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [queriedKey, setQueriedKey] = useState(null);  // 查詢當下的條件快照
  const [activeLevel, setActiveLevel] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);  // 哪一顆複製鈕剛按過（含單筆與整批）

  const currentKey = useMemo(
    () => JSON.stringify({ weeks: [...selectedWeeks].sort(), category, levelValue, topN }),
    [selectedWeeks, category, levelValue, topN]
  );
  const isStale = !!result && queriedKey !== null && queriedKey !== currentKey;

  // 只載入週期清單（輕量），不跑聚合
  useEffect(() => {
    const loadWeeks = async () => {
      try {
        const res = await axios.get(`${API_BASE}/game-stats/weeks`);
        if (res.data.code === 1) {
          const weeks = res.data.data.weeks || [];
          setAvailableWeeks(weeks);
          if (weeks.length > 0) setSelectedWeeks([weeks[0]]);  // 預設最新 1 週
        }
      } catch (err) {
        console.error("Failed to load weeks:", err);
      }
    };
    loadWeeks();
  }, []);

  // 類別 / 週期變動時載入可選級別（只是下拉選項，很輕）
  useEffect(() => {
    const loadLevels = async () => {
      if (category === "ALL" || selectedWeeks.length === 0) {
        setAvailableLevels([]);
        return;
      }
      setLoadingLevels(true);
      try {
        const res = await axios.get(`${API_BASE}/level-detail/levels`, {
          params: { category, weeks: selectedWeeks.join(",") },
        });
        if (res.data.success) {
          // top-players 以 sb/bb 分級（不分 ante），下拉去重
          const seen = new Set();
          const levels = [];
          (res.data.levels || []).forEach((lv) => {
            const key = `${lv.sb}_${lv.bb}`;
            if (seen.has(key)) return;
            seen.add(key);
            levels.push({ sb: lv.sb, bb: lv.bb, display: `${lv.sb}/${lv.bb}` });
          });
          setAvailableLevels(levels);
        } else {
          setAvailableLevels([]);
        }
      } catch (err) {
        setAvailableLevels([]);
      } finally {
        setLoadingLevels(false);
      }
    };
    loadLevels();
  }, [category, selectedWeeks]);

  const runQuery = async (weeksOverride) => {
    const weeks = weeksOverride && weeksOverride.length ? weeksOverride : selectedWeeks;
    if (weeks.length === 0) {
      setError("請先選擇週期");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = { weeks: weeks.join(","), category, top: topN };
      if (levelValue === LEVEL_ALL) {
        params.min_bb = 0;
      } else if (levelValue !== LEVEL_ALL_HIGH) {
        const [sb, bb] = levelValue.split("_");
        params.sb = sb;
        params.bb = bb;
      }

      const res = await axios.get(`${API_BASE}/level-top-players`, { params });
      if (res.data.success) {
        const data = res.data.data;
        setResult(data);
        setSelectedWeeks(weeks);
        setAppliedWeeks([...weeks]);
        setQueriedKey(JSON.stringify({ weeks: [...weeks].sort(), category, levelValue, topN }));
        setActiveLevel(data.levels.length ? data.levels[0].level : null);
      } else {
        setError(res.data.error || "查詢失敗");
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // 上/下 N 週：明確按鈕觸發，直接套用並重查（成本約 0.1 秒）
  const nav = windowNav(availableWeeks, appliedWeeks);
  const applyWindow = (win) => {
    if (!win || !win.length) return;
    setSelectedWeeks(win);
    runQuery(win);
  };

  // 複製文字；key 用來標記「這顆鈕剛按過」顯示打勾
  const copyText = (text, key) => {
    const done = () => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {});
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); done(); } catch (e) { /* ignore */ }
      document.body.removeChild(ta);
    }
  };

  // 表格內單筆複製鈕（只有 icon）
  const copyIcon = (value, ckey, title) => (
    <Button
      size="sm"
      className="p-0"
      title={title}
      onClick={() => copyText(value, ckey)}
      style={iconCopyBtnStyle(copiedKey === ckey)}
    >
      <i className={`fa fa-${copiedKey === ckey ? 'check' : 'copy'}`} />
    </Button>
  );

  const weeksLabel = appliedWeeks.length === 1
    ? appliedWeeks[0]
    : `${appliedWeeks[appliedWeeks.length - 1]} ~ ${appliedWeeks[0]}`;

  const current = result && result.levels.find((lv) => lv.level === activeLevel);

  return (
    <div className="content">
      {/* 標題原本埋在「查詢條件卡片」裡面。抽出來成為 header —— 那張卡片
          是**查詢表單**（選好條件按「查詢」才計算），屬於動作，留在原地。 */}
      <TinyPageHeader
        title="級別 Top 玩家"
        description="每個級別 Rake 最高的前 N 位真人玩家（已排除 EcoBot）。選好條件後按「查詢」才會計算。"
      />

      {/* ==================== 查詢條件卡片 ==================== */}
      <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}`, marginBottom: '20px' }}>
        <CardBody style={{ padding: '20px' }}>
          <Row>
            <Col xs={12} md={6} xl={3}>
              <FormGroup>
                <Label style={labelStyle}><i className="fa fa-calendar mr-2" />選擇週期</Label>
                {/* 下拉與「上/下 N 週」是同一組控制項 → 用同一個元件，
                    這一欄比較窄所以用 stacked（下拉在上、導航在下）。 */}
                <TinyWeekRangePicker
                  layout="stacked"
                  options={availableWeeks}
                  selectedValues={selectedWeeks}
                  onChange={setSelectedWeeks}
                  onApply={() => {}}
                  onCancel={() => setSelectedWeeks([...(appliedWeeks.length ? appliedWeeks : selectedWeeks)])}
                  triggerText={selectedWeeks.length > 0 ? `已選擇 ${selectedWeeks.length} 週` : "選擇週期"}
                  dropdownAlign="left"
                  dropdownMinWidth={360}
                  dropdownMaxWidth={560}
                  nav={nav}
                  onWindowChange={applyWindow}
                />
              </FormGroup>
            </Col>

            <Col xs={6} md={6} xl={2}>
              <FormGroup>
                <Label style={labelStyle}><i className="fa fa-gamepad mr-2" />遊戲類別</Label>
                <Input
                  type="select"
                  value={category}
                  onChange={(e) => { setCategory(e.target.value); setLevelValue(LEVEL_ALL_HIGH); }}
                  style={inputStyle}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            <Col xs={12} md={6} xl={3}>
              <FormGroup>
                <Label style={labelStyle}>
                  <i className="fa fa-layer-group mr-2" />級別
                  {loadingLevels && <TinyBusy style={{ marginLeft: 8 }} />}
                </Label>
                <Input type="select" value={levelValue} onChange={(e) => setLevelValue(e.target.value)} style={inputStyle}>
                  <option value={LEVEL_ALL_HIGH}>全部級別（5/10 以上）</option>
                  <option value={LEVEL_ALL}>全部級別（含 1/2、3/6）</option>
                  {availableLevels.filter((lv) => lv.bb >= 10).map((lv) => (
                    <option key={`${lv.sb}_${lv.bb}`} value={`${lv.sb}_${lv.bb}`}>只看 {lv.display}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            <Col xs={6} md={6} xl={2}>
              <FormGroup>
                <Label style={labelStyle}><i className="fa fa-sort-numeric-asc mr-2" />取前幾名</Label>
                <Input
                  type="select"
                  value={topN}
                  onChange={(e) => setTopN(parseInt(e.target.value, 10))}
                  style={inputStyle}
                >
                  {TOP_OPTIONS.map((n) => (
                    <option key={n} value={n}>前 {n} 名</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>

            <Col xs={12} md={12} xl={2}>
              <FormGroup>
                {/* 佔位 label：讓按鈕與同列輸入框底線對齊（窄螢幕時按鈕自成一列，不會被擠爆）*/}
                <Label style={{ ...labelStyle, visibility: 'hidden', display: 'block' }}>查詢</Label>
                <Button
                  block
                  disabled={loading || selectedWeeks.length === 0}
                  onClick={() => runQuery()}
                  style={primaryBtnStyle(loading || selectedWeeks.length === 0)}
                >
                  {loading ? <TinyBusy light /> : <><i className="fa fa-search mr-2" />查詢</>}
                </Button>
              </FormGroup>
            </Col>
          </Row>

          {isStale && (
            <div style={{ color: COLORS.warning, fontSize: '13px' }}>
              <i className="fa fa-exclamation-triangle mr-2" />
              條件已變更，下方為前一次查詢結果，請按「查詢」重新計算。
            </div>
          )}
        </CardBody>
      </Card>

      {error && (
        <Card style={{ background: COLORS.cardBg, border: `1px solid rgba(245,54,92,0.5)`, marginBottom: '20px' }}>
          <CardBody style={{ color: COLORS.danger, padding: '16px 20px' }}>
            <i className="fa fa-times-circle mr-2" />{error}
          </CardBody>
        </Card>
      )}

      {loading && <TinyLoading variant="block" text="計算中" />}

      {!loading && !result && !error && (
        <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
          <CardBody className="text-center" style={{ color: COLORS.textMuted, padding: '48px 16px' }}>
            <i className="fa fa-users fa-2x mb-3" style={{ display: 'block', color: COLORS.textDimmed }} />
            選好週期與級別後，按「查詢」開始計算。
          </CardBody>
        </Card>
      )}

      {!loading && result && (
        <>
          {/* ⚠️ 四個計數原本一格一張漸層 Card（1.8rem 圖示 + 1.6rem 數字，約 110px 高），
                 Brad 2026-09-10：太高 → 換成公版的 TinyStatStrip（約 57px）。
                 第一格是週期字串不是短數字，所以用 valueStyle 調小。 */}
          <TinyStatStrip items={[
            {
              label: appliedWeeks.length > 1 ? `查詢週期（${appliedWeeks.length} 週合計）` : "查詢週期",
              icon: 'fa-calendar', color: '17, 205, 239', iconColor: COLORS.info,
              value: weeksLabel,
              valueStyle: { fontSize: appliedWeeks.length > 1 ? '0.82rem' : '0.95rem', letterSpacing: '0.3px' },
            },
            {
              label: current ? `${current.level} 真人 Rake` : "真人 Rake",
              icon: 'fa-dollar', color: '102, 126, 234', iconColor: COLORS.accent,
              value: current ? numberWithCommas(current.real_rake) : "-",
            },
            {
              label: current ? `${current.level} 真人玩家數` : "真人玩家數",
              icon: 'fa-user', color: '45, 206, 137', iconColor: COLORS.success,
              value: current ? numberWithCommas(current.real_players) : "-",
            },
            {
              label: `前 ${result.meta.top_n} 名佔該級別真人 Rake`,
              icon: 'fa-pie-chart', color: '251, 99, 64', iconColor: COLORS.warning,
              value: current && current.real_rake
                ? `${((current.top_rake / current.real_rake) * 100).toFixed(1)}%`
                : "-",
            },
          ]} />

          {/* ==================== 結果卡片 ==================== */}
          <Card style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.border}` }}>
            <CardHeader style={{ background: COLORS.headerBg, borderBottom: `1px solid ${COLORS.border}` }}>
              <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '12px' }}>
                <CardTitle tag="h5" style={{ color: COLORS.textPrimary, marginBottom: 0, fontWeight: 600 }}>
                  <i className="fa fa-list mr-2" style={{ color: COLORS.accent }} />
                  各級別 Top {result.meta.top_n} 真人玩家
                  <Badge color="info" className="ml-2" style={{ fontSize: '11px' }}>{result.meta.category}</Badge>
                  <span style={{ color: COLORS.textMuted, fontSize: '12px', fontWeight: 400, marginLeft: '10px' }}>
                    已排除 EcoBot 玩家×級別 {numberWithCommas(result.meta.eco_excluded)} 筆
                  </span>
                </CardTitle>
              </div>
            </CardHeader>

            <CardBody style={{ padding: '20px' }}>
              {result.levels.length === 0 ? (
                <div className="text-center" style={{ color: COLORS.textMuted, padding: '32px 0' }}>
                  這個條件下沒有資料。
                </div>
              ) : (
                <>
                  {/* 每個級別一個 Tab。⚠️ 這批 tab 是**查詢結果**的一部分
                      （級別由結果決定），所以留在結果區裡，不上移到 header。 */}
                  <TinyPageTabs
                    /* ⚠️ 這批是同質清單（盲注級別），所以四個 tab 用**同一個**圖示 ——
                       每個級別各配一個不同圖示只會變成雜訊，而完全沒有圖示又跟
                       其他頁不一致（公版要求每個 tab 都有 icon）。 */
                    items={result.levels.map((lv) => ({
                      key: String(lv.level), label: lv.level, icon: "fa fa-coins",
                    }))}
                    active={String(activeLevel)}
                    onChange={(k) => setActiveLevel(
                      result.levels.find((lv) => String(lv.level) === k)?.level
                    )}
                  />

                  {/* 整批複製：放在級別 Tab 正下方，最容易被看到 */}
                  {current && (
                    <div className="d-flex justify-content-end" style={{ marginTop: '14px', gap: '8px' }}>
                      <Button
                        size="sm"
                        onClick={() => copyText(current.players.map((p) => p.player_id).join("\n"), 'bulk_id')}
                        style={subtleBtnStyle}
                      >
                        <i className={`fa fa-${copiedKey === 'bulk_id' ? 'check' : 'copy'} mr-2`} />
                        {copiedKey === 'bulk_id' ? '已複製 ' : '複製 '}
                        {current.players.length} 個玩家ID
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => copyText(current.players.map((p) => p.nickname).join("\n"), 'bulk_nick')}
                        style={subtleBtnStyle}
                      >
                        <i className={`fa fa-${copiedKey === 'bulk_nick' ? 'check' : 'copy'} mr-2`} />
                        {copiedKey === 'bulk_nick' ? '已複製 ' : '複製 '}
                        {current.players.length} 個玩家暱稱
                      </Button>
                    </div>
                  )}

                  <TabContent activeTab={activeLevel} style={{ paddingTop: '14px' }}>
                    {result.levels.map((lv) => (
                      <TabPane tabId={lv.level} key={lv.level}>
                        {activeLevel === lv.level && (
                          <div style={{ overflowX: 'auto' }}>
                            <Table responsive style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
                              <thead>
                                <tr style={{ color: COLORS.textMuted }}>
                                  <th style={{ ...thStyle, width: '60px' }}>排名</th>
                                  <th style={thStyle}>玩家ID</th>
                                  <th style={thStyle}>玩家暱稱</th>
                                  <th style={thStyle}>所屬俱樂部</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Rake</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>手數</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>水前輸贏（扣除JP）</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>佔級別真人 Rake</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lv.players.map((p) => (
                                  <tr key={p.player_id}>
                                    <td style={{ ...tdStyle, color: COLORS.textMuted, fontWeight: 600 }}>{p.rank}</td>
                                    <td style={{ ...tdStyle, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                      {p.player_id}
                                      {copyIcon(p.player_id, `id:${lv.level}:${p.player_id}`, "複製玩家ID")}
                                    </td>
                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                      {p.nickname}
                                      {copyIcon(p.nickname, `nick:${lv.level}:${p.player_id}`, "複製玩家暱稱")}
                                    </td>
                                    <td style={{ ...tdStyle, color: COLORS.textSecondary }}>
                                      {p.club_name}
                                      <span style={{ color: COLORS.textDimmed, fontSize: '11px' }}> ({p.club_id})</span>
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                                      {numberWithCommas(p.rake)}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{numberWithCommas(p.hands)}</td>
                                    <td style={{
                                      ...tdStyle,
                                      textAlign: 'right',
                                      color: p.pre_rake_pl_exclude_jp >= 0 ? COLORS.success : COLORS.danger,
                                    }}>
                                      {numberWithCommas(p.pre_rake_pl_exclude_jp)}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: COLORS.textSecondary }}>
                                      {p.rake_share.toFixed(2)}%
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </TabPane>
                    ))}
                  </TabContent>

                  <div style={{ color: COLORS.textDimmed, fontSize: '12px', marginTop: '16px', lineHeight: 1.8 }}>
                    <b style={{ color: COLORS.textMuted }}>Rake</b>：該玩家在該級別、所選週期內的 Rake 合計（排名依據）。
                    <b style={{ color: COLORS.textMuted }} className="ml-3">手數</b>：同口徑，只算該級別。
                    <b style={{ color: COLORS.textMuted }} className="ml-3">水前輸贏（扣除JP）</b>：Rake + 輸贏 + JP FEE − JP Payout，把 JP 影響剝乾淨後的純牌局輸贏。
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}

export default TinyTopPlayers;
